import { useState, useEffect } from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import PremiumLock from '../../components/Common/PremiumLock';
import { useScreenType } from '../../hooks/useScreenType';
import DesktopTechnicalAlerts from './DesktopTechnicalAlerts';
import MobileTechnicalAlerts from './MobileTechnicalAlerts';
import { 
  TechnicalAlert, 
  SIGNAL_TYPES, 
} from './TechnicalAlertsTypes';

const TechnicalAlertsScreen: React.FC = () => {
  const { token, hasPermission } = useAuth();
  const { isMobile } = useScreenType();
  const [alerts, setAlerts] = useState<TechnicalAlert[]>([]);
  const [stocks, setStocks] = useState<{ symbole: string; nom_societe?: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [filterType, setFilterType] = useState('ALL');
  const [filterSeverity, setFilterSeverity] = useState('ALL');
  const [filterSymbol, setFilterSymbol] = useState('ALL');
  const [filterDays, setFilterDays] = useState<number>(30); // Default: 1 mois (30 jours)
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const [showSettings, setShowSettings] = useState(false);
  const [disabledSignals, setDisabledSignals] = useState<Set<string>>(new Set());

  const isPro = hasPermission('alertes_pro') || hasPermission('admin_platform');

  const fetchStocks = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/stocks/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStocks(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching stocks:', err);
    }
  };

  const fetchAlerts = async (daysCutoff: number = filterDays) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/v1/technical-alerts?limit=500&days=${daysCutoff}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAlerts(data);
      } else if (response.status === 403) {
        setError('Cette fonctionnalité est réservée aux abonnés Pro.');
      } else {
        setError('Erreur lors du chargement des alertes.');
      }
    } catch (err) {
      setError('Erreur réseau.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/user/technical-alerts/preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDisabledSignals(new Set(data.disabled_alert_types || []));
      }
    } catch (err) {
      console.error('Error fetching preferences:', err);
    }
  };

  useEffect(() => {
    if (isPro && token) {
      fetchStocks();
      fetchPreferences();
    }
  }, [token, isPro]);

  useEffect(() => {
    if (isPro && token) {
      fetchAlerts(filterDays);
    }
  }, [token, isPro, filterDays]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, filterSeverity, filterSymbol, filterDays]);

  const savePreferences = async (disabled: Set<string>) => {
    try {
      await fetch(`${API_URL}/v1/user/technical-alerts/preferences`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ disabled_alert_types: [...disabled] }),
      });
    } catch (err) {
      console.error('Error saving preferences:', err);
    }
  };

  const toggleSignal = (key: string) => {
    const next = new Set(disabledSignals);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setDisabledSignals(next);
    savePreferences(next);
  };

  const enableAll = () => {
    const next = new Set<string>();
    setDisabledSignals(next);
    savePreferences(next);
  };

  const getSignalInfo = (type: string) =>
    SIGNAL_TYPES.find(s => s.key === type) || { key: type, label: type, emoji: '📡', category: 'Autre' };

  // Filter alerts
  const filteredAlerts = alerts.filter(a => {
    if (disabledSignals.has(a.signal_type)) return false;
    if (filterType !== 'ALL' && a.signal_type !== filterType) return false;
    if (filterSeverity !== 'ALL' && a.severity !== filterSeverity) return false;
    if (filterSymbol !== 'ALL' && a.symbole.toUpperCase() !== filterSymbol.toUpperCase()) return false;
    return true;
  });

  // Pagination calculations
  const totalAlerts = filteredAlerts.length;
  const totalPages = Math.ceil(totalAlerts / pageSize) || 1;
  const safePage = Math.min(Math.max(currentPage, 1), totalPages);

  const paginatedAlerts = filteredAlerts.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Group paginated alerts by date
  const groupedByDate = paginatedAlerts.reduce<Record<string, TechnicalAlert[]>>((acc, alert) => {
    const key = alert.date_signal;
    if (!acc[key]) acc[key] = [];
    acc[key].push(alert);
    return acc;
  }, {});

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (!isPro) {
    return (
      <SiteLayout>
        <div style={{ padding: 40, maxWidth: 900, margin: '0 auto' }}>
          <PremiumLock
            title="Signaux Pro"
            description="Identifiez les opportunités sur les 48 sociétés de la BRVM avec KaneaBourse. Accédez à nos signaux techniques avancés et aux analyses de valorisation générées par l'IA Nia. Ne naviguez plus à vue : optimisez votre stratégie d'investissement avec la performance de Kanea Pro."
          />
        </div>
      </SiteLayout>
    );
  }

  const commonProps = {
    filteredAlerts,
    disabledSignals,
    filterType,
    setFilterType,
    filterSeverity,
    setFilterSeverity,
    filterSymbol,
    setFilterSymbol,
    filterDays,
    setFilterDays,
    stocks,
    currentPage: safePage,
    setCurrentPage,
    totalPages,
    totalAlerts,
    pageSize,
    setPageSize,
    loading,
    error,
    groupedByDate,
    toggleSignal,
    enableAll,
    formatDate,
    getSignalInfo,
  };

  return (
    <SiteLayout>
      {isMobile ? (
        <MobileTechnicalAlerts {...commonProps} />
      ) : (
        <DesktopTechnicalAlerts 
          {...commonProps} 
          showSettings={showSettings} 
          setShowSettings={setShowSettings} 
        />
      )}
    </SiteLayout>
  );
};

export default TechnicalAlertsScreen;
